import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Flame, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal({ open, onClose, onAuth, locked = false }) {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingDone, setPendingDone] = useState(false);

  useEffect(() => {
    if (open) setPendingDone(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && !locked && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      window.__lenis?.start();
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, locked]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload =
        mode === "register" ? { email, password, name } : { email, password };
      const { data } = await api.post(`/auth/${mode}`, payload);
      if (mode === "register" && data.pending) {
        setPendingDone(true);
        return;
      }
      onAuth(data);
      onClose();
      toast.success(
        mode === "register"
          ? `Welcome aboard, ${data.name}`
          : `Welcome back, ${data.name}`
      );
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="auth-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={locked ? undefined : onClose}
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-5 backdrop-blur-sm"
        >
          <motion.div
            key="auth-card"
            data-testid="auth-modal"
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="w-full max-w-sm border border-[#27272A] bg-[#0D0D0D]"
          >
            <div className="flex items-center justify-between border-b border-[#27272A] px-6 py-4">
              <span className="flex items-center gap-2 font-jb text-[10px] tracking-[0.35em] text-zinc-500">
                <Flame className="h-3.5 w-3.5 text-[#FF3B30]" />
                CREW ACCESS
              </span>
              {!locked && (
                <button
                  data-testid="auth-close-btn"
                  onClick={onClose}
                  aria-label="Close login"
                  className="grid h-8 w-8 place-items-center border border-[#27272A] text-zinc-400 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="px-6 pt-6">
              {pendingDone ? (
                <div data-testid="register-pending-msg" className="pb-2 text-center">
                  <Flame className="mx-auto h-7 w-7 text-[#FFC107]" />
                  <p className="mt-5 font-chivo text-lg font-black tracking-tight text-white">
                    Signal received
                  </p>
                  <p className="mt-3 font-jb text-[10px] leading-relaxed tracking-[0.15em] text-zinc-400">
                    YOUR ACCOUNT IS WAITING FOR ADMIN APPROVAL.
                    <br />
                    YOU CAN LOG IN ONCE AN ADMIN APPROVES IT.
                  </p>
                  <button
                    data-testid="auth-back-to-login-btn"
                    onClick={() => {
                      setPendingDone(false);
                      setMode("login");
                    }}
                    className="mt-6 w-full border border-zinc-700 py-3 font-jb text-[11px] tracking-[0.25em] text-zinc-300 transition-colors duration-300 hover:border-[#FF3B30] hover:text-white"
                  >
                    BACK TO LOGIN
                  </button>
                </div>
              ) : (
                <>
              <div className="grid grid-cols-2 border border-[#27272A]">
                {["login", "register"].map((m) => (
                  <button
                    key={m}
                    data-testid={`auth-tab-${m}`}
                    onClick={() => {
                      setMode(m);
                      setError("");
                      setPendingDone(false);
                    }}
                    className={`py-2.5 font-jb text-[11px] tracking-[0.25em] transition-colors duration-300 ${
                      mode === m
                        ? "bg-[#FF3B30]/15 text-[#FF5247]"
                        : "text-zinc-500 hover:text-zinc-300"
                    }`}
                  >
                    {m === "login" ? "LOG IN" : "REGISTER"}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="mt-6 space-y-4">
                {mode === "register" && (
                  <input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="CALLSIGN / NAME"
                    required
                    maxLength={60}
                    className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                  />
                )}
                <input
                  data-testid="auth-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="EMAIL"
                  required
                  className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                />
                <input
                  data-testid="auth-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={
                    mode === "register" ? "PASSWORD (MIN 8 CHARS)" : "PASSWORD"
                  }
                  required
                  minLength={mode === "register" ? 8 : 1}
                  className="w-full border border-[#27272A] bg-black/40 px-4 py-3 font-jb text-xs tracking-[0.15em] text-white placeholder:text-zinc-600 focus:border-[#FF3B30] focus:outline-none"
                />
                {error && (
                  <p
                    data-testid="auth-error"
                    className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-3 py-2 font-jb text-[10px] tracking-[0.15em] text-[#FF5247]"
                  >
                    {error}
                  </p>
                )}
                <button
                  data-testid="auth-submit-btn"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#FF3B30] py-3.5 font-jb text-xs font-bold tracking-[0.3em] text-black transition-colors duration-300 hover:bg-[#FFC107] disabled:opacity-50"
                >
                  {busy
                    ? "STANDBY…"
                    : mode === "login"
                      ? "LOG IN"
                      : "CREATE ACCOUNT"}
                </button>
              </form>

              <p className="py-6 text-center font-jb text-[9px] leading-relaxed tracking-[0.25em] text-zinc-600">
                MEMBERS ONLY — NEW ACCOUNTS
                <br />
                NEED ADMIN APPROVAL.
              </p>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
